import React, { useState, useEffect } from 'react';
import { Zap, ArrowRight, DollarSign } from 'lucide-react';
import { getArbitrage } from '../../api/dashboard';

export default function Arbitrage({ onCardClick }: { onCardClick?: (id: string) => void }) {
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [minMargin, setMinMargin] = useState(5);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getArbitrage(minMargin, 30)
      .then(d => setOpportunities(d.opportunities || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [minMargin]);

  const getRoute = (opp: any) => {
    const routes = [
      { label: 'CM FR → eBay FR', profit: Number(opp.profit_cm_fr_to_ebay_fr) || 0 },
      { label: 'CM FR → CM EU', profit: Number(opp.profit_fr_to_eu) || 0 },
      { label: 'CM EU → eBay FR', profit: Number(opp.profit_cm_eu_to_ebay_fr) || 0 },
    ];
    return routes.sort((a, b) => b.profit - a.profit)[0];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Zap size={24} className="text-yellow-400" /> Arbitrage Radar
          </h2>
          <p className="text-sm text-neutral-500 mt-1">Opportunites d'arbitrage cross-marketplace (frais inclus)</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-400">Profit min:</span>
          {[2, 5, 10, 20].map(m => (
            <button key={m} onClick={() => setMinMargin(m)}
              className={`px-3 py-1.5 rounded-lg text-sm ${minMargin === m ? 'bg-yellow-500 text-black font-medium' : 'bg-surface text-neutral-400 hover:bg-neutral-800'}`}>
              {m}€
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-neutral-500">Analyse des marches...</div>
      ) : opportunities.length === 0 ? (
        <div className="text-center py-20 text-neutral-500">Aucune opportunite avec {minMargin}€ de profit minimum</div>
      ) : (
        <div className="space-y-3">
          {opportunities.map((opp, i) => {
            const best = getRoute(opp);
            return (
              <div key={`${opp.id}-${i}`} onClick={() => onCardClick?.(opp.id)} className="bg-surface border border-border rounded-2xl p-5 hover:border-yellow-500/30 transition-colors cursor-pointer">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img src={`${process.env.REACT_APP_API_URL || ''}/img/opus-assets/cards/${opp.id}.png`} alt="" className="w-10 h-14 rounded flex-shrink-0 object-cover bg-neutral-700" />
                    <div>
                      <div className="text-sm font-medium text-white">{opp.name_fr || opp.name}</div>
                      <div className="text-xs text-neutral-500">{opp.id} — {opp.rarity}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-yellow-400">+{best.profit.toFixed(2)}€</div>
                    <div className="text-xs text-neutral-500 flex items-center gap-1 justify-end">
                      {best.label} <ArrowRight size={12} />
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {[
                    { label: 'CM FR → eBay FR', profit: opp.profit_cm_fr_to_ebay_fr },
                    { label: 'CM FR → CM EU', profit: opp.profit_fr_to_eu },
                    { label: 'CM EU → eBay FR', profit: opp.profit_cm_eu_to_ebay_fr },
                  ].map(route => (
                    <div key={route.label} className={`rounded-xl p-3 text-center ${Number(route.profit) > 0 ? 'bg-green-500/5 border border-green-500/20' : 'bg-neutral-800/50'}`}>
                      <div className="text-xs text-neutral-500">{route.label}</div>
                      <div className={`text-sm font-bold mt-1 ${Number(route.profit) > 0 ? 'text-green-400' : 'text-neutral-600'}`}>
                        {route.profit ? `${Number(route.profit) > 0 ? '+' : ''}${Number(route.profit).toFixed(2)}€` : '—'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
